import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close, AutoAwesome, Palette } from '@mui/icons-material';
import { useI18n } from '../../i18n';
import { dashboardApi } from '../../api';
import { DashboardTopicValue } from '../../types';
import {
  SignalsDefinition,
  SIGNALS_MQTT_EFFECTS,
  SignalsMqttEffect,
  signalsBaseTopic,
  signalsColorToHex,
  signalsColorTopic,
  signalsEffectTopic,
  signalsSignalTopic,
  signalsTopicSegment,
} from '../types';
import { SIGNAL_PRESET_COLORS } from '../createDefaultSignals';
import { resolveLiveStatusesFromMqtt } from '../resolveLiveStatus';
import SignalsPreview, { signalSlotKey } from './SignalsPreview';
import RadialChoiceMenu, { RadialChoice } from './RadialChoiceMenu';

const DEFAULT_EFFECT_COLOR = '#ff0000';
const EFFECT_PRESET_COLORS = SIGNAL_PRESET_COLORS.filter((preset) => preset.id !== 'off');

type MenuKind = 'status' | 'effect' | 'color';

interface OpenMenu {
  kind: MenuKind;
  slotId?: string;
  anchor: HTMLElement;
}

export interface SignalsCommandDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  definition: SignalsDefinition | null | undefined;
  /** Catalog stacklight id / widget signalName — transport resolved on the server. */
  signalName?: string;
  /** Instance id and its host; the same definition may run on several instances. */
  stacklight?: string;
  hostname?: string;
  baseTopic?: string;
  values?: Record<string, DashboardTopicValue | null | undefined> | null;
  onPublished?: () => void | Promise<void>;
}

function mqttToken(values: SignalsCommandDialogProps['values'], topic: string): string {
  const raw = values?.[topic]?.raw;
  return typeof raw === 'string' ? raw.trim() : '';
}

const SignalsCommandDialog: React.FC<SignalsCommandDialogProps> = ({
  open,
  onClose,
  title,
  definition,
  signalName: signalNameProp,
  stacklight,
  hostname,
  baseTopic: baseTopicProp,
  values,
  onPublished,
}) => {
  const { t } = useI18n();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const shellRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [cmdError, setCmdError] = useState<string | null>(null);
  const [menu, setMenu] = useState<OpenMenu | null>(null);

  const signalName = String(
    signalNameProp || (definition as { signalName?: string } | null | undefined)?.signalName
      || definition?.metadata?.name || '',
  ).trim();
  const baseTopic = signalsBaseTopic(baseTopicProp || definition);
  const effectTopic = signalsEffectTopic(baseTopic);
  const colorTopic = signalsColorTopic(baseTopic);
  const mqttColor = signalsColorToHex(values?.[colorTopic]?.raw);
  const [color, setColor] = useState(mqttColor || DEFAULT_EFFECT_COLOR);
  const effectRaw = mqttToken(values, effectTopic).toLowerCase();
  const currentEffect = (SIGNALS_MQTT_EFFECTS as readonly string[]).includes(effectRaw)
    ? effectRaw as SignalsMqttEffect
    : null;
  const resolved = useMemo(
    () => resolveLiveStatusesFromMqtt(definition, values, {
      preferPreviewWhenEmpty: true,
      baseTopic,
    }),
    [definition, values, baseTopic],
  );

  useEffect(() => {
    if (!open) {
      setMenu(null);
      return;
    }
    if (mqttColor) setColor(mqttColor);
  }, [open, mqttColor]);

  const sendCommand = useCallback(async (leaf: string, payload: string) => {
    if (!signalName && !baseTopic) {
      setCmdError(t('dashboard.signalsNoTopic'));
      return;
    }
    setBusy(true);
    setCmdError(null);
    try {
      if (signalName) {
        await dashboardApi.commandStacklight({
          signalName,
          stacklight: stacklight || undefined,
          hostname: hostname || undefined,
          leaf,
          payload,
          baseTopic: baseTopic || undefined,
        });
      } else {
        // Legacy fallback when no catalog signalName is known.
        const topic = leaf === 'color' ? colorTopic
          : leaf === 'effect' ? effectTopic
            : signalsSignalTopic(baseTopic, leaf);
        await dashboardApi.publishMqtt(topic, payload);
      }
      await onPublished?.();
    } catch (err: any) {
      setCmdError(err?.response?.data?.error || err?.message || t('dashboard.signalsPublishFailed'));
    } finally {
      setBusy(false);
    }
  }, [signalName, stacklight, hostname, baseTopic, colorTopic, effectTopic, onPublished, t]);

  const setSlotStatus = async (slotName: string, statusName: string) => {
    // The leaf is a topic segment on every transport, so a slot named
    // `Signal 4` addresses the same slot over MQTT and over Zigbee.
    await sendCommand(signalsTopicSegment(slotName), statusName);
  };

  const sendColor = async (hex: string) => {
    const normalized = signalsColorToHex(hex) || hex;
    setColor(normalized);
    await sendCommand('color', normalized);
  };

  const sendEffect = async (effect: SignalsMqttEffect) => {
    if (color && effect !== 'off') {
      await sendCommand('color', color);
    }
    await sendCommand('effect', effect);
  };

  const radialItems: RadialChoice[] = useMemo(() => {
    if (!menu) return [];
    if (menu.kind === 'status') {
      const slot = (definition?.signals || []).find((item, index) => (
        signalSlotKey(item, index) === menu.slotId || item.id === menu.slotId || item.name === menu.slotId
      ));
      const live = resolved.find((row) => row.slotId === menu.slotId || row.slotName === menu.slotId);
      return (slot?.statuses || []).map((status) => ({
        id: status.id || status.name,
        label: status.name,
        color: status.color,
        active: live?.statusId === status.id || live?.name === status.name,
      }));
    }
    if (menu.kind === 'effect') {
      return SIGNALS_MQTT_EFFECTS.map((effect) => ({
        id: effect,
        label: t(`dashboard.signalsEffect.${effect}`),
        color: effect === 'off' ? '#000000' : (color || DEFAULT_EFFECT_COLOR),
        active: currentEffect === effect,
      }));
    }
    return EFFECT_PRESET_COLORS.map((preset) => ({
      id: preset.color,
      label: t(preset.labelKey),
      color: preset.color,
      active: color.toLowerCase() === preset.color.toLowerCase(),
    }));
  }, [menu, definition, resolved, t, color, currentEffect]);

  const handleRadialSelect = async (id: string) => {
    if (!menu) return;
    const open = menu;
    setMenu(null);
    if (open.kind === 'status') {
      const slot = (definition?.signals || []).find((item, index) => (
        signalSlotKey(item, index) === open.slotId || item.id === open.slotId || item.name === open.slotId
      ));
      const status = slot?.statuses.find((item) => item.id === id || item.name === id);
      if (slot && status) {
        await setSlotStatus(slot.name, status.name);
      }
      return;
    }
    if (open.kind === 'effect') {
      await sendEffect(id as SignalsMqttEffect);
      return;
    }
    await sendColor(id);
  };

  const openTool = (kind: 'effect' | 'color', el: HTMLElement) => {
    setMenu((prev) => (prev?.kind === kind ? null : { kind, anchor: el }));
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (menu && (reason === 'escapeKeyDown' || reason === 'backdropClick')) {
          setMenu(null);
          return;
        }
        onClose();
      }}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: 'background.default',
          backgroundImage: 'none',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          height: fullScreen ? '100%' : 'min(720px, calc(100dvh - 16px))',
          maxHeight: 'calc(100dvh - 16px)',
          m: fullScreen ? 0 : 1,
        },
      }}
      TransitionProps={{ onExited: () => { setCmdError(null); setMenu(null); } }}
    >
      <Box
        ref={shellRef}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          height: '100%',
          overflow: 'hidden',
        }}
      >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          px: 2,
          pt: 1.5,
          pb: 0.5,
          gap: 1,
        }}
      >
        <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }} noWrap>
          {title}
        </Typography>
        <IconButton onClick={onClose} aria-label={t('common.close')} size="small">
          <Close />
        </IconButton>
      </Box>
      <DialogContent
        sx={{
          pt: 0,
          px: { xs: 1, sm: 2 },
          pb: 0,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        {cmdError && (
          <Alert severity="error" sx={{ mb: 1.5, width: '100%', flexShrink: 0 }} onClose={() => setCmdError(null)}>
            {cmdError}
          </Alert>
        )}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            px: 1,
            py: 1,
          }}
        >
          <SignalsPreview
            definition={definition || undefined}
            signals={definition?.signals}
            resolvedStatuses={resolved}
            size="large"
            fill
            showLabels={false}
            selectedSlotId={menu?.kind === 'status' ? menu.slotId : undefined}
            onSelectSlot={(slotId, el) => {
              if (!el) return;
              setMenu({ kind: 'status', slotId, anchor: el });
            }}
            capActive={Boolean(currentEffect && currentEffect !== 'off')}
          />
        </Box>
      </DialogContent>
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          px: 2,
          pt: 0.5,
          pb: 1,
        }}
      >
        <Tooltip title={t('dashboard.signalsEffectHub')}>
          <IconButton
            color={currentEffect && currentEffect !== 'off' ? 'primary' : 'default'}
            aria-label={t('dashboard.signalsEffectHub')}
            aria-pressed={menu?.kind === 'effect'}
            onClick={(event) => openTool('effect', event.currentTarget)}
            size="large"
          >
            <AutoAwesome />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('dashboard.signalsColorHub')}>
          <IconButton
            aria-label={t('dashboard.signalsColorHub')}
            aria-pressed={menu?.kind === 'color'}
            onClick={(event) => openTool('color', event.currentTarget)}
            size="large"
            sx={{ color }}
          >
            <Palette />
          </IconButton>
        </Tooltip>
      </Box>
      {!menu && (
        <Typography variant="caption" color="text.secondary" align="center" sx={{ px: 2, pb: 1.5, flexShrink: 0 }}>
          {t('dashboard.signalsTapHint')}
        </Typography>
      )}
      {menu && shellRef.current && radialItems.length > 0 && (
        <RadialChoiceMenu
          anchorEl={menu.anchor}
          containerEl={shellRef.current}
          items={radialItems}
          disabled={busy}
          onSelect={handleRadialSelect}
          onClose={() => setMenu(null)}
        />
      )}
      </Box>
    </Dialog>
  );
};

export default SignalsCommandDialog;
